// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Col, Row, Button } from "@components";
import type { StepProps } from "@components";
import type { ServiceKpis } from "@modules/service/types";
import { split } from "@transformers/schema";
import { CommonComplianceKpisForm } from "@modules/service/components/common/common-compliance-kpis";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?AbsenceMonitoring,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepKpis extends React.Component<Props> {
  form: *;
  validateKpis: *;

  componentDidMount() {
    this.validateKpis = new Ajv().compile(this.schema.kpisList);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validateKpis = new Ajv().compile(this.schema.kpisList);
    }
  }

  get data() {
    const draft = this.props.draft;
    return {
      kpis: draft?.content?.kpis || null,
    };
  }

  get schema() {
    return {
      kpisList: ((this.props.schema || {}).properties || {}).kpis,
      kpis: (((this.props.schema || {}).properties || {}).kpis || {}).items,
    };
  }

  isValid = (data?: Object) => {
    const { kpis } = data || {};
    if (!Array.isArray(kpis) || kpis.length === 0) {
      return false;
    }

    return kpis.every(({ activeFrom, activeTo, kpiMeasure }) => {
      if (!activeFrom || !kpiMeasure) {
        return false;
      }

      if (!activeTo) {
        return true;
      }

      const from = Date.parse(activeFrom);
      const to = Date.parse(activeTo);

      return !Number.isNaN(from) && !Number.isNaN(to) && from < to;
    });
  };

  handleFormRef = (ref: *) => {
    this.form = ref;
  };

  submitKpis = ({ formData }: Object) => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data, true);
    }
  };

  updateKpis = (kpis: Array<ServiceKpis>) => {
    const next = {
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        kpis,
      },
    };
    this.props.setDraft(next);
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12">
            {this.schema.kpis && this.data ? (
              <CommonComplianceKpisForm
                data={this.data.kpis}
                schema={this.schema.kpis}
                onChange={this.updateKpis}
                kpiTitle={this.props.t("KPI_TARGET_HOURS")}
              />
            ) : null}
          </Col>
        </Row>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button onClick={this.submitKpis} disabled={!this.isValid(this.data)}>
              {this.props.t("ABSENCE_MONITORING_STEP2_NEXT_BUTTON")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAbsenceMonitoringSchema(state),
    draft: serviceSelectors.getAbsenceMonitoringDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAbsenceMonitoringDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepKpis);
